//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Button, CircularProgress, Paper, TableBody, TextField, Typography } from '@mui/material'

import { EthAddr } from '@shared/SchemaTypes'
import React, { useEffect } from 'react'

import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useOtChainState } from '@react/redux/store'
import { yupEthAddr } from '@shared/Enums'

import {
  fetchAllPoolAccountAccessPairs,
  fetchAllPoolAccountAccessPairsRegenerateForPool,
} from '@react/fetchers/PoolFetchers'

import { companyProfilesGet } from '@react/fetchers/companyProfileFetchers'
import { allowPoolParticipation } from '@react/signedTxs/allowPoolParticipationTx'
import { logOT } from '@shared/loggingShared'

interface AddrStatus {
  userAddr: EthAddr
  status: boolean
  ensName: string
  companyName: string
}

export default function AdminGrantPoolAccessPage(props: {
  poolAddr: EthAddr
  setHasSomeGranted?: (value: boolean) => void
}): React.ReactElement {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { signer },
    webSocket,
    chainConfiguration: { chainConfigurationName, providerReadOnly },
  } = useOtChainState()

  const [userStatuses, setUserStatuses] = React.useState<AddrStatus[]>([])
  const [showSpinnerAction, setShowSpinnerAction] = React.useState<boolean>(false)
  const [accessControlAddr, setAccessControlAddr] = React.useState<EthAddr>('')
  const [accessControlAddrError, setAccessControlAddrError] = React.useState<string | undefined>(undefined)

  useEffect(() => {
    getAllParticipants().catch((error) => {
      logOT.error(error)
    })
  }, [providerReadOnly, props.poolAddr])

  const grant = (userAddr: EthAddr): void => {
    if (providerReadOnly && userAddr && signer) {
      logOT.debug('grant', userAddr, props.poolAddr)
      setShowSpinnerAction(true)
      allowPoolParticipation(webSocket, signer, props.poolAddr, userAddr, chainConfigurationName)
        .then(() => {
          return getAllParticipants()
        })
        .finally(() => {
          setShowSpinnerAction(false)
        })
        .catch((error) => {
          logOT.error(error)
        })
    }
  }
  const handleAllowAddress = (): void => {
    logOT.debug('grant', accessControlAddr, props.poolAddr)
    grant(accessControlAddr)
  }

  async function getAllParticipants(): Promise<void> {
    if (providerReadOnly && props.poolAddr && serverApiBaseUrl && restAPIKey) {
      const companyProfiles = await companyProfilesGet(serverApiBaseUrl)

      const poolAccountPairs = await fetchAllPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey)
      logOT.info('fetchPoolAccountAccessPairRegeneration users', poolAccountPairs)
      const userSet = new Set<EthAddr>()
      for (const item of poolAccountPairs) {
        userSet.add(item.accountAddr)
      }
      const allUsers: EthAddr[] = Array.from(userSet)
      const userStatusesCalculating: AddrStatus[] = []

      for (const userAddr of allUsers) {
        const pair = poolAccountPairs.find((item) => item.accountAddr === userAddr && item.poolAddr === props.poolAddr)
        const companyProfile = companyProfiles.find((profile) => profile.wallets.includes(userAddr))
        userStatusesCalculating.push({
          userAddr,
          status: !!pair,
          ensName: '',
          companyName: companyProfile?.companyName ?? '',
        })
      }
      setUserStatuses(userStatusesCalculating)
    }
  }
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value
    setAccessControlAddr(newValue)
    if (!yupEthAddr.isValidSync(newValue)) {
      setAccessControlAddrError('Invalid address format')
    } else {
      if (!providerReadOnly) {
        setAccessControlAddrError('Connect to a wallet first')
      } else {
        setAccessControlAddrError(undefined)
      }
    }
  }
  if (!providerReadOnly || !signer) {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Typography variant="inherit" color="textSecondary">
          Connect before managing pool access
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {showSpinnerAction && (
        <div>
          <CircularProgress /> <Typography> Updating access list</Typography>
        </div>
      )}
      {!showSpinnerAction && (
        <TableContainer sx={{ maxHeight: 800 }}>
          <div>
            <TextField
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                'data-testid': 'accessControlAddr',
              }}
              value={accessControlAddr}
              label="Lender  WalletAddr"
              fullWidth
              placeholder={'0x1234...'}
              margin="dense"
              onChange={handleOnChange}
              error={accessControlAddrError != null}
            />
            <Typography variant="inherit" color="textSecondary">
              {accessControlAddrError}
            </Typography>

            <Button
              disabled={!!accessControlAddrError || accessControlAddr?.length === 0}
              sx={{
                float: 'right',
                backgroundColor: '#171717',
                color: '#FFFFFF',
              }}
              variant="contained"
              onClick={handleAllowAddress}
            >
              Allow Address
            </Button>
          </div>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell key="action" style={{ minWidth: 20 }}>
                  Access
                </TableCell>
                <TableCell key="address" style={{ minWidth: 20 }}>
                  Address
                </TableCell>
                <TableCell key="ens" style={{ minWidth: 20 }}>
                  Company Name
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userStatuses.map((userStatus, addr) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={addr}>
                    <TableCell key="action" style={{ minWidth: 20 }}>
                      {userStatus.status ? (
                        'Granted'
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            grant(userStatus.userAddr)
                          }}
                        >
                          Grant
                        </Button>
                      )}
                    </TableCell>
                    <TableCell key="address" style={{ minWidth: 20 }}>
                      {userStatus.userAddr}
                    </TableCell>
                    <TableCell key="ens" style={{ minWidth: 20 }}>
                      {userStatus.companyName}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <div style={{ marginTop: 30 }}>
            <Button
              variant="contained"
              onClick={() => {
                fetchAllPoolAccountAccessPairsRegenerateForPool(serverApiBaseUrl, restAPIKey, props.poolAddr)
                  .then(() => {
                    return getAllParticipants()
                  })
                  .then(() => {
                    logOT.info('Regenerate Access List from Chain SUCCESS')
                  })
                  .catch((error) => {
                    logOT.error(error)
                  })
              }}
            >
              Regenerate Access List from Chain
            </Button>
          </div>
        </TableContainer>
      )}
    </Paper>
  )
}
